if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      }

      connectedCallback() {
        this.filterCustomMobileGallery();

        document.addEventListener('change', this.boundHandleVariantChange);
        document.addEventListener('click', this.boundHandleVariantChange);

        this.bindCustomMobileThumbnails();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (!button) return;

          button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        document.removeEventListener('change', this.boundHandleVariantChange);
        document.removeEventListener('click', this.boundHandleVariantChange);
      }

      handleVariantChange(event) {
        if (
          event.target.matches?.('.product-form__input input[type="radio"], .product-form__input select') ||
          event.target.closest?.('.product-form__input label')
        ) {
          this.runMobileFilterRepeatedly();
        }
      }

      runMobileFilterRepeatedly() {
        this.filterCustomMobileGallery();
        setTimeout(() => this.filterCustomMobileGallery(), 150);
        setTimeout(() => this.filterCustomMobileGallery(), 400);
        setTimeout(() => this.filterCustomMobileGallery(), 900);
      }

      getSelectedVariantKeys() {
        const productInfo = this.closest('product-info') || document.querySelector('product-info');
        const selectedVariantJson = productInfo?.querySelector('variant-selects [data-selected-variant]')?.textContent;

        if (selectedVariantJson) {
          try {
            const variant = JSON.parse(selectedVariantJson);
            const options = Array.isArray(variant.options) ? variant.options : [];

            return {
              fullVariant: options.join(' / ').trim(),
              firstOption: (variant.option1 || options[0] || '').trim(),
            };
          } catch (error) {
            console.warn('No se pudo leer la variante seleccionada para filtrar imágenes.', error);
          }
        }

        const selectedOptions = Array.from(
          productInfo?.querySelectorAll('.product-form__input input[type="radio"]:checked, .product-form__input select') || []
        )
          .map((input) => input.value.trim())
          .filter(Boolean);

        return {
          fullVariant: selectedOptions.join(' / ').trim(),
          firstOption: (selectedOptions[0] || '').trim(),
        };
      }

      mediaMatchesVariant(element) {
        const alt = (element?.dataset?.variantAlt || '').trim();
        const selectedVariant = this.getSelectedVariantKeys();

        return (
          alt === '' ||
          alt === selectedVariant.fullVariant ||
          alt === selectedVariant.firstOption
        );
      }

      filterCustomMobileGallery() {
        const mainItems = Array.from(this.querySelectorAll('[data-mobile-main-media]'));
        const thumbItems = Array.from(this.querySelectorAll('[data-mobile-thumb]'));

        mainItems.forEach((item) => {
          const show = this.mediaMatchesVariant(item);

          item.hidden = !show;
          item.style.setProperty('display', show ? '' : 'none', 'important');
          item.classList.remove('is-active');
        });

        thumbItems.forEach((item) => {
          const show = this.mediaMatchesVariant(item);

          item.hidden = !show;
          item.style.setProperty('display', show ? '' : 'none', 'important');
          item.removeAttribute('aria-current');
        });

        const firstVisibleMain = mainItems.find((item) => !item.hidden);
        const firstVisibleThumb = thumbItems.find((item) => !item.hidden);

        if (firstVisibleMain) {
          firstVisibleMain.classList.add('is-active');
          firstVisibleMain.style.setProperty('display', 'block', 'important');
        }

        if (firstVisibleThumb) {
          firstVisibleThumb.setAttribute('aria-current', 'true');
        }

        this.bindCustomMobileThumbnails();
      }

      bindCustomMobileThumbnails() {
        this.querySelectorAll('[data-mobile-thumb]').forEach((thumb) => {
          if (thumb.dataset.mobileThumbBound === 'true') return;

          thumb.dataset.mobileThumbBound = 'true';

          thumb.addEventListener('click', () => {
            const mediaId = thumb.dataset.mobileMediaId;

            this.querySelectorAll('[data-mobile-main-media]').forEach((mainItem) => {
              const isActive = mainItem.dataset.mobileMediaId === mediaId && !mainItem.hidden;

              mainItem.classList.toggle('is-active', isActive);
              mainItem.style.setProperty('display', isActive ? 'block' : 'none', 'important');
            });

            this.querySelectorAll('[data-mobile-thumb]').forEach((button) => {
              if (button === thumb) {
                button.setAttribute('aria-current', 'true');
              } else {
                button.removeAttribute('aria-current');
              }
            });
          });
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
